import { h } from '../utils/dom.js';
import { iconSvg } from '../utils/icons.js';
import { Search, Calendar, Clock, MapPin, Monitor, Globe, Users, ChevronDown, MoreVertical, ArrowRight } from 'lucide';
import { Avatar, DEFAULT_AVATAR_COLOR } from './avatar.js';

export const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
};

export const PageTabs = ({ tabs, activeTab: initial, onTabChange }) => {
  let active = initial;
  const btns = tabs.map((t) =>
    h('button', { class: `page-tab${t.id === active ? ' active' : ''}`, type: 'button' }, t.label)
  );
  btns.forEach((btn, i) => {
    btn.onclick = () => {
      active = tabs[i].id;
      btns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      onTabChange(tabs[i].id);
    };
  });
  return h('div', { class: 'page-tabs' }, ...btns);
};

export const SearchBar = ({ placeholder, oninput, value }) =>
  h('div', { class: 'search-bar' },
    h('span', { class: 'search-bar-icon', html: iconSvg(Search, 16) }),
    h('input', { class: 'search-bar-input', type: 'search', placeholder: placeholder || 'Buscar…', oninput, value }),
  );

export const SegmentedControl = ({ options, value: initial, onChange }) => {
  let active = initial;
  const btns = options.map((opt) =>
    h('button', { class: `seg-opt${opt.value === active ? ' active' : ''}`, type: 'button' },
      opt.icon ? h('span', { class: 'seg-icon', html: iconSvg(opt.icon, 14) }) : null,
      opt.label,
    )
  );
  btns.forEach((btn, i) => {
    btn.onclick = () => {
      active = options[i].value;
      btns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      onChange(options[i].value);
    };
  });
  return h('div', { class: 'segmented' }, ...btns);
};

export const SettingsLayout = ({ tabs, activeTab: initial, onTabChange }) => {
  let active = initial;
  const btns = tabs.map((t) =>
    h('button', { type: 'button', class: t.id === active ? 'active' : '' }, t.label)
  );
  btns.forEach((btn, i) => {
    btn.onclick = () => {
      active = tabs[i].id;
      btns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      onTabChange(tabs[i].id);
    };
  });
  const nav = h('div', { class: 'settings-nav' }, ...btns);
  return h('div', { class: 'settings-layout' },
    nav,
    h('div', { class: 'settings-content' }),
  );
};

export const EventCard = ({ time, title, hostName, hostColor, meta, badge, thumbIcon, thumbColor, actions, onclick }) => {
  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onclick?.();
    }
  };
  return h('div', { class: 'event-card', tabindex: '0', role: 'button', onclick, onkeydown: handleKey },
    thumbIcon
      ? h('div', { class: 'event-card-thumb', style: `background: ${thumbColor || 'var(--c-accent)'}` },
          h('span', { style: 'color:#fff;opacity:.8', html: iconSvg(thumbIcon, 20) }),
        )
      : null,
    h('div', { class: 'event-card-body' },
      time ? h('div', { class: 'event-card-time' }, time) : null,
      h('div', { class: 'event-card-title' }, title),
      hostName ? h('div', { class: 'event-card-host' },
        Avatar(hostName, hostColor || DEFAULT_AVATAR_COLOR, 20),
        h('span', {}, hostName),
      ) : null,
      meta?.length ? h('div', { class: 'event-card-meta' }, ...meta) : null,
      badge ? h('div', { style: { marginTop: '8px' } }, badge) : null,
    ),
    actions?.length
      ? h('div', { class: 'event-card-actions' }, ...actions)
      : null,
  );
};

export const EventListGrouped = ({ groups, groupKey }) =>
  h('div', { class: 'event-list-group-parent' },
    ...groups.map((g) =>
      h('div', { class: 'event-list-group' },
        h('div', { class: 'event-list-date' }, g[groupKey] || ''),
        h('div', { class: 'event-list-items' }, ...g.items),
      )
    ),
  );

export const AvatarGroup = ({ users, max = 3, size = 28 }) => {
  const visible = users.slice(0, max);
  const remaining = users.length - max;
  return h('div', { class: 'row gap-xs', style: { alignItems: 'center' } },
    ...visible.map((u) => Avatar(u.name, u.color, size)),
    remaining > 0
      ? h('span', { class: 'muted text-xs', style: { marginLeft: '4px' } }, `+${remaining}`)
      : null,
  );
};

export const memberSummary = (items, max = 3) => {
  if (!items?.length) return h('span', { class: 'muted text-sm' }, 'Sin miembros');
  const visible = items.slice(0, max);
  const rest = items.length - max;
  const names = visible.map((m) => m.full_name || m.name).filter(Boolean);
  const text = names.join(', ') + (rest > 0 ? ` y ${rest} más` : '');
  return h('span', { class: 'text-sm' }, text);
};

let activeDropdown = null;

export const DropdownMenu = ({ items }) => {
  const wrap = h('div', { class: 'dropdown-wrap', style: { position: 'relative', display: 'inline-block' } });
  const toggleBtn = h('button', { class: 'btn btn-ghost btn-sm', type: 'button', 'aria-label': 'Más opciones' },
    h('span', { html: iconSvg(MoreVertical, 14) }),
  );
  const menu = h('div', { class: 'dropdown-menu', role: 'menu', style: { display: 'none', position: 'absolute', right: 0, top: '100%', zIndex: 50, background: 'var(--c-panel)', border: '1px solid var(--c-border)', borderRadius: '10px', padding: '4px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', minWidth: '160px' } });
  
  for (const item of items) {
    menu.appendChild(h('button', { class: 'dropdown-item', type: 'button', style: { display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: '13px', border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: '6px' }, 
      onclick: (e) => { closeDropdown(); item.onclick?.(e); } 
    }, item.label));
  }
  
  function closeDropdown() {
    menu.style.display = 'none';
    if (activeDropdown === wrap) activeDropdown = null;
  }
  
  toggleBtn.onclick = () => {
    if (activeDropdown && activeDropdown !== wrap) {
      const prevMenu = activeDropdown.querySelector('.dropdown-menu');
      if (prevMenu) prevMenu.style.display = 'none';
    }
    const isOpen = menu.style.display === 'block';
    menu.style.display = isOpen ? 'none' : 'block';
    activeDropdown = isOpen ? null : wrap;
  };
  
  wrap.append(toggleBtn, menu);
  return wrap;
};

document.addEventListener('click', (e) => {
  if (activeDropdown && !activeDropdown.contains(e.target)) {
    const menu = activeDropdown.querySelector('.dropdown-menu');
    if (menu) menu.style.display = 'none';
    activeDropdown = null;
  }
});

export const EntityIcon = ({ type, size = 16 }) => {
  const icons = { session: Calendar, user: Users, branch: MapPin, service: Globe };
  const icon = icons[type] || Calendar;
  return h('span', { style: { lineHeight: 0 }, html: iconSvg(icon, size) });
};
